import React, { useMemo } from "react";
import { Helmet } from "react-helmet-async";
import {
  SITE,
  PAGES,
  DEFAULT_DESCRIPTION,
  absoluteUrl,
  ogImageUrl,
  buildGraphLd,
} from "../seo/seoConfig";

/**
 * Per-route SEO: title, description, canonical, Open Graph, Twitter, robots, JSON-LD.
 * Public pages default to index,follow. Auth-only pages (profile) are noindex.
 */
export default function SeoHead({
  page = "home",
  title: titleOverride,
  description: descriptionOverride,
  noindex,
  canonicalPath: canonicalPathOverride,
}) {
  const spec = PAGES[page] || PAGES.home;
  const title = titleOverride || spec.title;
  const description = descriptionOverride || spec.description || DEFAULT_DESCRIPTION;
  const canonicalPath = canonicalPathOverride || spec.canonicalPath || "/";
  const canonical = absoluteUrl(canonicalPath);
  const image = ogImageUrl();
  const shouldNoindex = noindex ?? spec.noindex ?? false;
  const robots = shouldNoindex
    ? "noindex, nofollow, noarchive, nosnippet"
    : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";

  const jsonLd = useMemo(
    () =>
      JSON.stringify(
        buildGraphLd({
          title,
          description,
          canonical,
          pagePath: canonicalPath,
        })
      ),
    [title, description, canonical, canonicalPath]
  );

  const googleVerify = process.env.REACT_APP_GOOGLE_SITE_VERIFICATION || "";
  const bingVerify = process.env.REACT_APP_BING_SITE_VERIFICATION || "";

  return (
    <Helmet prioritizeSeoTags>
      <html lang={SITE.language} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={SITE.keywords} />
      <meta name="author" content={SITE.brand} />
      <meta name="creator" content={SITE.brand} />
      <meta name="publisher" content={SITE.brand} />
      <meta name="application-name" content={SITE.name} />
      <meta name="apple-mobile-web-app-title" content={SITE.shortName} />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="theme-color" content={SITE.themeColor} />
      <meta name="color-scheme" content="light" />
      <meta name="format-detection" content="telephone=no" />
      <meta name="referrer" content="strict-origin-when-cross-origin" />
      <meta name="robots" content={robots} />
      <meta name="googlebot" content={robots} />
      <meta name="bingbot" content={robots} />
      <link rel="canonical" href={canonical} />
      <link rel="alternate" hrefLang="en" href={canonical} />
      <link rel="alternate" hrefLang="x-default" href={canonical} />

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE.name} />
      <meta property="og:locale" content={SITE.locale} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:image:alt" content={SITE.imageAlt} />
      <meta property="og:image:type" content="image/png" />
      <meta property="og:image:width" content="1024" />
      <meta property="og:image:height" content="1024" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={SITE.imageAlt} />

      {googleVerify ? <meta name="google-site-verification" content={googleVerify} /> : null}
      {bingVerify ? <meta name="msvalidate.01" content={bingVerify} /> : null}

      <script type="application/ld+json">{jsonLd}</script>
    </Helmet>
  );
}
